import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import httpClient from 'Service'
import { AnnouncementSchoolOption, SchoolAnnouncement, SchoolAnnouncementPayload, SchoolAnnouncementsResponse } from '../types'

const QUERY_KEY = ['announcement-management']
const ANNOUNCEMENT_PAGE_SIZE = 10
const ANNOUNCEMENT_SORT = 'createdDate,DESC'

type ListParams = {
  page: number
  search?: string
  schoolId?: number
}

const getAnnouncementId = (raw: unknown): number | undefined => {
  if (!raw || typeof raw !== 'object') return undefined

  const data = raw as {
    id?: number
    data?: { id?: number }
    object?: { id?: number }
  }

  return data.id ?? data.data?.id ?? data.object?.id
}

const getAnnouncements = async ({ page, search, schoolId }: ListParams) => {
  const response = await httpClient.get<SchoolAnnouncementsResponse>('/api/announcement-management', {
    params: {
      page,
      size: ANNOUNCEMENT_PAGE_SIZE,
      sort: ANNOUNCEMENT_SORT,
      ...(search ? { search } : {}),
      ...(schoolId ? { schoolId } : {}),
    },
  })
  return response.data
}

const getAnnouncementDetail = async (id: string) => {
  const response = await httpClient.get<SchoolAnnouncement | { data?: SchoolAnnouncement; object?: SchoolAnnouncement }>(
    `/api/announcement-management/${id}`,
  )
  const data = response.data

  if ('data' in data && data.data) return data.data
  if ('object' in data && data.object) return data.object

  return data as SchoolAnnouncement
}

const getSchoolOptions = async (): Promise<AnnouncementSchoolOption[]> => {
  const response = await httpClient.get<AnnouncementSchoolOption[]>('/api/account/schools')
  return response.data
}

const getAnnouncementSchoolFilterOptions = async (): Promise<AnnouncementSchoolOption[]> => {
  const response = await httpClient.get<AnnouncementSchoolOption[]>('/api/announcement-management/schools')
  return response.data
}

const createAnnouncement = async (payload: SchoolAnnouncementPayload) => {
  const response = await httpClient.post<SchoolAnnouncement | { data?: SchoolAnnouncement; object?: SchoolAnnouncement }>(
    '/api/announcement-management',
    payload,
  )
  return response.data
}

const updateAnnouncement = async ({ id, payload }: { id: number; payload: SchoolAnnouncementPayload }) => {
  const response = await httpClient.patch<SchoolAnnouncement>(`/api/announcement-management/${id}`, payload)
  return response.data
}

const deleteAnnouncement = async (id: number) => {
  const response = await httpClient.delete(`/api/announcement-management/${id}`)
  return response.data
}

const uploadAnnouncementAttachment = async ({ announcementId, file }: { announcementId: number; file: File }) => {
  const formData = new FormData()
  formData.append('files', file)

  const response = await httpClient.post(`/api/announcement-management/${announcementId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return response.data
}

const deleteAnnouncementAttachment = async ({ announcementId, attachmentId }: { announcementId: number; attachmentId: number }) => {
  const response = await httpClient.delete(`/api/announcement-management/${announcementId}/attachments/${attachmentId}`)
  return response.data
}

export const useSchoolAnnouncements = ({
  includeSchools = false,
  page = 0,
  search,
  schoolId,
}: {
  includeSchools?: boolean
  page?: number
  search?: string
  schoolId?: number
} = {}) => {
  const queryClient = useQueryClient()

  const listQuery = useQuery({
    queryKey: [...QUERY_KEY, page, search, schoolId],
    queryFn: () => getAnnouncements({ page, search, schoolId }),
    keepPreviousData: true,
  })

  const schoolsQuery = useQuery({
    queryKey: ['announcement-school-options'],
    queryFn: getSchoolOptions,
    enabled: includeSchools,
  })

  const schoolFiltersQuery = useQuery({
    queryKey: ['announcement-school-filter-options'],
    queryFn: getAnnouncementSchoolFilterOptions,
    enabled: includeSchools,
  })

  const createMutation = useMutation({
    mutationFn: createAnnouncement,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  })

  const updateMutation = useMutation({
    mutationFn: updateAnnouncement,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  })

  const deleteMutation = useMutation({
    mutationFn: deleteAnnouncement,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  })

  const uploadAttachmentMutation = useMutation({
    mutationFn: uploadAnnouncementAttachment,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  })

  const deleteAttachmentMutation = useMutation({
    mutationFn: deleteAnnouncementAttachment,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  })

  return {
    announcements: listQuery.data?.data ?? [],
    totalCount: listQuery.data?.totalCount ?? 0,
    totalPages: listQuery.data?.totalPages ?? 0,
    schoolOptions: schoolsQuery.data ?? [],
    schoolFilterOptions: schoolFiltersQuery.data ?? [],
    getAnnouncementId,
    isLoading: listQuery.isLoading,
    isFetching: listQuery.isFetching,
    isSchoolsLoading: schoolsQuery.isLoading,
    isSchoolsFetching: schoolsQuery.isFetching,
    isSchoolFiltersLoading: schoolFiltersQuery.isLoading,
    isSchoolFiltersFetching: schoolFiltersQuery.isFetching,
    createMutation,
    updateMutation,
    deleteMutation,
    uploadAttachmentMutation,
    deleteAttachmentMutation,
  }
}

export const useSchoolAnnouncementDetail = (id?: string) => {
  const detailQuery = useQuery({
    queryKey: [...QUERY_KEY, 'detail', id],
    queryFn: () => getAnnouncementDetail(id as string),
    enabled: !!id,
  })

  return {
    announcement: detailQuery.data,
    isLoading: detailQuery.isLoading,
    isFetching: detailQuery.isFetching,
  }
}
